/* eslint-disable camelcase */
import * as React from "react"
import { Appearance, ViewStyle } from "react-native"
import { observer } from "mobx-react-lite"
import { Actionsheet, ActionsheetProps } from "../actionsheet/actionsheet"
import { FC, PropsWithChildren, useCallback, useState } from "react"
import { FormControl } from "../form-control/form-control"
import { Button } from "../button/button"
import { LazyRender } from "../lazy-render/lazy-render"
import { delay } from "../../utils/delay"
import { color } from "../../theme"
import { DateTimePicker } from "../date-time-picker/date-time-picker"
import { Radio, View } from "native-base"
import { translate, TxKeyPath } from "../../i18n"
import { Text } from "../text/text"
import JalaAnalytic from "../../utils/jala-analytic"
import _ from "lodash"

export interface DateTimeFilterProps extends PropsWithChildren<any> {
  value?: {
    date_after?: string,
    date_before?: string,
    time?: string
  },

  isDisabled?: boolean,

  isOpen?: boolean,
  onClose?: () => void,

  onValueChange?: (value: any) => any

  actionsheetProps?: ActionsheetProps,

  colorScheme?: "light" | "dark",
  actionSheetColorScheme?: "light" | "dark"
  clearable?: boolean,
}

const FORM_CONTROL: ViewStyle = {
  paddingHorizontal: 16,
  marginBottom: 8
}

const BUTTON_CONTAINER: ViewStyle = {
  flexDirection: "row",
  paddingBottom: 16,
  paddingHorizontal: 16,
}

const ACTIONSHEET: ViewStyle = {
  minHeight: 0,
  height: undefined
}

const BUTTON: ViewStyle = {
  flex: 1
}

const ActionsheetComponent: FC<{
  colorScheme?: "light" | "dark"
  defaultValue?: {
    date_after?: string,
    date_before?: string,
    time?: string
  },
  onValueChange: (value: any) => void
  withTime?: boolean
}> = ({
  colorScheme,
  defaultValue = {},
  onValueChange: onValueChangeProp,
  withTime = false
}) => {
  const [ dateAfter, setDateAfter ] = useState<string>(defaultValue.date_after)
  const [ dateBefore, setDateBefore ] = useState<string>(defaultValue.date_before)
  const [ time, setTime ] = useState<string>(defaultValue.time)

  const onReset = useCallback(async () => {
    await delay(1)
    onValueChangeProp && onValueChangeProp({})
  }, [onValueChangeProp])

  const onApply = useCallback(async () => {
    await delay(1)
    onValueChangeProp && onValueChangeProp(JSON.parse(JSON.stringify({
      date_after: dateAfter || undefined,
      date_before: dateBefore || undefined,
      time: time || undefined
    })))
  }, [dateAfter, dateBefore, onValueChangeProp, time])

  return (
    <>
      <FormControl
        labelTx={"dateTimeFilter.dateAfter"}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        style={FORM_CONTROL}
      >
        <DateTimePicker
          colorScheme={colorScheme}
          value={dateAfter}
          onValueChange={setDateAfter}
        />
      </FormControl>
      <FormControl
        labelTx={"dateTimeFilter.dateBefore"}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        style={FORM_CONTROL}
      >
        <DateTimePicker
          colorScheme={colorScheme}
          value={dateBefore}
          onValueChange={setDateBefore}
        />
      </FormControl>
      {withTime && <FormControl
        labelTx={"dateTimeFilter.time"}
        colorScheme={colorScheme}
        labelColor={color.palette.gray800}
        style={FORM_CONTROL}
      >
        <Radio.Group
          colorScheme={colorScheme}
          name="time"
          accessibilityLabel={translate("dateTimeFilter.time")}
          value={time || ""}
          onChange={setTime}
        >
          <View flexGrow={1} flexDirection="row" justifyContent="space-between">
            {["", "am", "pm"].map((time, index) => (
              <Radio key={`radio-time-${index}`} mr="36px" value={time}>
                <Text tx={(time && `dateTimeFilter.${time}` || `dateTimeFilter.all`) as TxKeyPath} ml="8px"
                      colorScheme={colorScheme} />
              </Radio>
            ))}
          </View>
        </Radio.Group>
      </FormControl>}
      <View style={BUTTON_CONTAINER}>
        <Button
          testID="jala:id/reset-date-time-filter-button"
          tx="dateTimeFilter.reset"
          preset="outline"
          colorScheme={colorScheme}
          style={BUTTON}
          onPress={onReset}
        />
        <View width="16px" />
        <Button
          testID="jala:id/apply-date-time-filter-button"
          tx="dateTimeFilter.apply"
          colorScheme={colorScheme}
          style={BUTTON}
          onPress={onApply}
        />
      </View>
    </>
  );
}

/**
 * Describe your component here
 */
export const DateTimeFilter = observer(function DateTimeFilter(props: DateTimeFilterProps) {
  const {
    value = {},
    isOpen, onClose,
    onValueChange: onValueChangeProp,
    actionsheetProps,
    colorScheme = Appearance.getColorScheme(),
    actionSheetColorScheme = colorScheme,
    clearable,
    withTime = false
  } = props

  const onValueChange: (value: any) => void = useCallback((value: any) => {
    if (value && !_.isEmpty(value)) JalaAnalytic.log('Mobile app: pond - apply filter');
    onValueChangeProp && onValueChangeProp(value)
    onClose()
  }, [onClose, onValueChangeProp])

  return (
    <Actionsheet
      colorScheme={actionSheetColorScheme}
      isOpen={isOpen}
      onClose={onClose}
      clearable={clearable}
      actionsheetStyle={ACTIONSHEET}
      {...actionsheetProps}
    >
      <LazyRender>
        <ActionsheetComponent
          colorScheme={colorScheme}
          defaultValue={value}
          onValueChange={onValueChange}
          withTime={withTime}
        />
      </LazyRender>
    </Actionsheet>
  )
})
